/**
 * CSV File Dropzone Component
 *
 * Drag-and-drop file selection with react-dropzone.
 * Validates file type (CSV only) and size (max 150MB).
 */

import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Upload, AlertCircle, FileText, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CsvFileDropzoneProps {
  /** Callback when a valid file is accepted */
  onFileAccepted: (file: File) => void;
  /** Callback when file validation fails */
  onFileError: (error: string) => void;
  /** Currently selected file (for displaying selection) */
  selectedFile?: File | null;
  /** Callback to clear the selected file */
  onClearFile?: () => void;
  /** Whether the dropzone is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string | null;
}

/** Maximum file size: 150MB */
const MAX_SIZE = 150 * 1024 * 1024;

/** Accepted MIME types for CSV files */
const ACCEPTED_MIME_TYPES = {
  'text/csv': ['.csv'],
  'text/plain': ['.csv'],
  'application/vnd.ms-excel': ['.csv'],
};

/**
 * Format file size in human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * CSV File Dropzone Component
 */
export function CsvFileDropzone({
  onFileAccepted,
  onFileError,
  selectedFile,
  onClearFile,
  disabled = false,
  error,
}: CsvFileDropzoneProps) {
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = error || localError;

  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      setLocalError(null);

      // Handle rejections
      if (rejections.length > 0) {
        const rejection = rejections[0];
        const code = rejection.errors[0]?.code;

        let errorMessage = 'Invalid file';

        if (code === 'file-too-large') {
          errorMessage = `File exceeds ${formatFileSize(MAX_SIZE)} limit`;
        } else if (code === 'file-invalid-type') {
          errorMessage = 'Please upload a CSV file (.csv)';
        } else if (code === 'too-many-files') {
          errorMessage = 'Please upload only one file at a time';
        }

        setLocalError(errorMessage);
        onFileError(errorMessage);
        return;
      }

      // Handle accepted file
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Additional validation: check file extension
        if (!file.name.toLowerCase().endsWith('.csv')) {
          const errorMessage = 'Please upload a CSV file (.csv)';
          setLocalError(errorMessage);
          onFileError(errorMessage);
          return;
        }

        // Check if file is empty
        if (file.size === 0) {
          const errorMessage = 'File is empty';
          setLocalError(errorMessage);
          onFileError(errorMessage);
          return;
        }

        onFileAccepted(file);
      }
    },
    [onFileAccepted, onFileError]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME_TYPES,
    maxSize: MAX_SIZE,
    multiple: false,
    disabled,
    noClick: !!selectedFile, // Disable click when file is selected
    noKeyboard: !!selectedFile, // Disable keyboard when file is selected
  });

  // If a file is selected, show the selected file view
  if (selectedFile) {
    return (
      <div className="border-2 border-dashed border-primary/50 rounded-lg p-6 bg-primary/5">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            <FileText className="h-10 w-10 text-primary" aria-hidden="true" />
          </div>
          <div className="flex-grow min-w-0">
            <p className="font-medium text-foreground truncate" title={selectedFile.name}>
              {selectedFile.name}
            </p>
            <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
          </div>
          {onClearFile && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onClearFile}
              aria-label="Remove selected file"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'hover:border-primary hover:bg-primary/5',
        isDragActive && !isDragReject && 'border-primary bg-primary/10',
        isDragReject && 'border-destructive bg-destructive/10',
        displayError && 'border-destructive',
        disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:bg-transparent'
      )}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <input {...getInputProps()} aria-describedby="upload-help upload-error" />

      <div className="space-y-3">
        <Upload
          className={cn(
            'mx-auto h-12 w-12',
            isDragActive && !isDragReject ? 'text-primary' : 'text-muted-foreground',
            isDragReject && 'text-destructive'
          )}
          aria-hidden="true"
        />

        {isDragReject ? (
          <p className="text-destructive font-medium">Invalid file type</p>
        ) : isDragActive ? (
          <p className="text-primary font-medium">Drop your CSV file here</p>
        ) : (
          <>
            <div>
              <button
                type="button"
                className="font-medium text-primary hover:text-primary/80 focus:outline-none"
                onClick={open}
                disabled={disabled}
              >
                Click to upload
              </button>
              <span className="text-muted-foreground"> or drag and drop</span>
            </div>
            <p id="upload-help" className="text-xs text-muted-foreground">
              CSV files only (max {formatFileSize(MAX_SIZE)})
            </p>
          </>
        )}

        {displayError && (
          <div
            id="upload-error"
            className="flex items-center justify-center gap-2 text-sm text-destructive"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{displayError}</span>
          </div>
        )}
      </div>
    </div>
  );
}
